/**
 * Minimal crop + rotate dialog for candidate photos.
 * Square crop output as JPEG blob (≤5 MB). No external crop library.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Crop, RotateCw, ZoomIn, ZoomOut } from 'lucide-react';
import { toast } from 'sonner';

const OUTPUT_SIZE = 512;
const MAX_BYTES = 5 * 1024 * 1024;
/** Must match the h-64 / w-64 preview box. */
const STAGE_SIZE = 256;
const MIN_CROP = 48;
const NUDGE = 4;
/** Seeded inset so the dim, the border, and the handles all read as a movable box. */
const DEFAULT_CROP_RATIO = 0.8;

const HANDLES = [
  { id: 'nw', className: '-left-[7px] -top-[7px] cursor-nwse-resize' },
  { id: 'ne', className: '-right-[7px] -top-[7px] cursor-nesw-resize' },
  { id: 'sw', className: '-bottom-[7px] -left-[7px] cursor-nesw-resize' },
  { id: 'se', className: '-bottom-[7px] -right-[7px] cursor-nwse-resize' },
];

async function canvasToJpegBlob(canvas, quality = 0.9) {
  let q = quality;
  let blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', q));
  while (blob && blob.size > MAX_BYTES && q > 0.45) {
    q -= 0.1;
    blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', q));
  }
  return blob;
}

/** Part of the displayed image that is actually visible inside the stage. */
function visibleBounds(box) {
  const left = Math.max(0, box.x);
  const top = Math.max(0, box.y);
  const right = Math.min(STAGE_SIZE, box.x + box.w);
  const bottom = Math.min(STAGE_SIZE, box.y + box.h);
  return { left, top, right, bottom, w: right - left, h: bottom - top };
}

function defaultCrop(box) {
  const b = visibleBounds(box);
  const max = Math.min(b.w, b.h);
  if (max < 1) return null;
  const size = Math.max(Math.min(MIN_CROP, max), max * DEFAULT_CROP_RATIO);
  return { x: b.left + (b.w - size) / 2, y: b.top + (b.h - size) / 2, size };
}

function clampCrop(rect, box) {
  const b = visibleBounds(box);
  const size = Math.min(rect.size, b.w, b.h);
  if (size < 1) return null;
  return {
    size,
    x: Math.min(Math.max(rect.x, b.left), b.right - size),
    y: Math.min(Math.max(rect.y, b.top), b.bottom - size),
  };
}

function clampSize(value, max) {
  return Math.max(MIN_CROP, Math.min(value, Math.max(MIN_CROP, max)));
}

/** Corner drag keeps the selection square by anchoring the opposite corner. */
function resizeCrop(start, mode, px, py, b) {
  const right = start.x + start.size;
  const bottom = start.y + start.size;
  if (mode === 'se') {
    const size = clampSize(Math.max(px - start.x, py - start.y), Math.min(b.right - start.x, b.bottom - start.y));
    return { x: start.x, y: start.y, size };
  }
  if (mode === 'ne') {
    const size = clampSize(Math.max(px - start.x, bottom - py), Math.min(b.right - start.x, bottom - b.top));
    return { x: start.x, y: bottom - size, size };
  }
  if (mode === 'sw') {
    const size = clampSize(Math.max(right - px, py - start.y), Math.min(right - b.left, b.bottom - start.y));
    return { x: right - size, y: start.y, size };
  }
  const size = clampSize(Math.max(right - px, bottom - py), Math.min(right - b.left, bottom - b.top));
  return { x: right - size, y: bottom - size, size };
}

/**
 * Map the on-screen selection back to source pixels of the unrotated image.
 * Rotation is always a multiple of 90°, so the selection stays axis-aligned.
 */
function cropSourceRect(crop, box, natural, rotation) {
  const rotatedW = rotation % 180 === 0 ? natural.w : natural.h;
  const scale = box.w / rotatedW;
  if (!scale || !Number.isFinite(scale)) return null;
  const toOriginal = (x, y) => {
    const u = (x - box.x) / scale;
    const v = (y - box.y) / scale;
    if (rotation === 90) return { x: v, y: natural.h - u };
    if (rotation === 180) return { x: natural.w - u, y: natural.h - v };
    if (rotation === 270) return { x: natural.w - v, y: u };
    return { x: u, y: v };
  };
  const a = toOriginal(crop.x, crop.y);
  const b = toOriginal(crop.x + crop.size, crop.y + crop.size);
  const x = Math.max(0, Math.min(a.x, b.x));
  const y = Math.max(0, Math.min(a.y, b.y));
  const size = Math.min(Math.abs(b.x - a.x), Math.abs(b.y - a.y), natural.w - x, natural.h - y);
  if (size < 1) return null;
  return { x, y, size };
}

export function PhotoCropDialog({
  open,
  onOpenChange,
  imageSrc,
  onConfirm,
  title = 'Edit photo',
}) {
  const imgRef = useRef(null);
  const stageRef = useRef(null);
  const dragRef = useRef(null);
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const [busy, setBusy] = useState(false);
  const [cropMode, setCropMode] = useState(false);
  const [cropRect, setCropRect] = useState(null);
  const [imgBox, setImgBox] = useState(null);

  useEffect(() => {
    if (!open) return;
    setRotation(0);
    setZoom(1);
    setNatural({ w: 0, h: 0 });
    setCropMode(false);
    setCropRect(null);
    setImgBox(null);
  }, [open, imageSrc]);

  const onImgLoad = useCallback((e) => {
    const img = e.currentTarget;
    setNatural({ w: img.naturalWidth, h: img.naturalHeight });
  }, []);

  const previewStyle = useMemo(() => ({
    transform: `rotate(${rotation}deg) scale(${zoom})`,
    transformOrigin: 'center center',
    maxWidth: '100%',
    maxHeight: '100%',
  }), [rotation, zoom]);

  // Rotating or zooming changes the displayed geometry, so the selection restarts.
  const dropCrop = () => {
    setCropRect(null);
    setImgBox(null);
  };

  // Measure what the browser actually rendered so the mapping matches the CSS transform.
  useLayoutEffect(() => {
    if (!open || !cropMode || !natural.w) return;
    const stage = stageRef.current;
    const img = imgRef.current;
    if (!stage || !img) return;
    const s = stage.getBoundingClientRect();
    const i = img.getBoundingClientRect();
    const box = { x: i.left - s.left, y: i.top - s.top, w: i.width, h: i.height };
    setImgBox(box);
    setCropRect((prev) => (prev ? clampCrop(prev, box) : defaultCrop(box)));
  }, [open, cropMode, rotation, zoom, natural.w, natural.h]);

  const beginDrag = (e, mode) => {
    if (!cropRect || !imgBox) return;
    const stage = stageRef.current?.getBoundingClientRect();
    if (!stage) return;
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = {
      mode,
      stage,
      start: cropRect,
      px: e.clientX - stage.left,
      py: e.clientY - stage.top,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onDragMove = (e) => {
    const drag = dragRef.current;
    if (!drag || !imgBox) return;
    const x = e.clientX - drag.stage.left;
    const y = e.clientY - drag.stage.top;
    if (drag.mode === 'move') {
      setCropRect(clampCrop({
        size: drag.start.size,
        x: drag.start.x + (x - drag.px),
        y: drag.start.y + (y - drag.py),
      }, imgBox));
      return;
    }
    setCropRect(resizeCrop(drag.start, drag.mode, x, y, visibleBounds(imgBox)));
  };

  const endDrag = (e) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  const dragProps = (mode) => ({
    onPointerDown: (e) => beginDrag(e, mode),
    onPointerMove: onDragMove,
    onPointerUp: endDrag,
    onPointerCancel: endDrag,
  });

  const onSelectionKeyDown = (e) => {
    if (!cropRect || !imgBox) return;
    const step = e.shiftKey ? NUDGE * 4 : NUDGE;
    const delta = {
      ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step],
    }[e.key];
    if (!delta) return;
    e.preventDefault();
    setCropRect(clampCrop({ ...cropRect, x: cropRect.x + delta[0], y: cropRect.y + delta[1] }, imgBox));
  };

  // Outside crop mode the round preview shows the selected region only.
  const appliedCropStyle = useMemo(() => {
    if (cropMode || !cropRect) return undefined;
    const scale = STAGE_SIZE / cropRect.size;
    return {
      transform: `scale(${scale}) translate(${-cropRect.x}px, ${-cropRect.y}px)`,
      transformOrigin: '0 0',
    };
  }, [cropMode, cropRect]);

  const handleConfirm = async () => {
    const img = imgRef.current;
    if (!img || !natural.w) return;
    setBusy(true);
    let file;
    try {
      const canvas = document.createElement('canvas');
      canvas.width = OUTPUT_SIZE;
      canvas.height = OUTPUT_SIZE;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
      ctx.translate(OUTPUT_SIZE / 2, OUTPUT_SIZE / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      const selected = cropRect && imgBox
        ? cropSourceRect(cropRect, imgBox, natural, rotation)
        : null;
      if (selected) {
        ctx.drawImage(
          img, selected.x, selected.y, selected.size, selected.size,
          -OUTPUT_SIZE / 2, -OUTPUT_SIZE / 2, OUTPUT_SIZE, OUTPUT_SIZE,
        );
      } else {
        const base = Math.min(natural.w, natural.h);
        const crop = base / zoom;
        const sx = (natural.w - crop) / 2;
        const sy = (natural.h - crop) / 2;
        ctx.drawImage(img, sx, sy, crop, crop, -OUTPUT_SIZE / 2, -OUTPUT_SIZE / 2, OUTPUT_SIZE, OUTPUT_SIZE);
      }
      const blob = await canvasToJpegBlob(canvas);
      if (!blob) throw new Error('Could not encode image');
      if (blob.size > MAX_BYTES) throw new Error('Image exceeds 5 MB after compression');
      file = new File([blob], 'candidate-photo.jpg', { type: 'image/jpeg' });
    } catch (err) {
      setBusy(false);
      toast.error(err?.name === 'SecurityError'
        ? 'This image could not be read for editing. Upload it again to replace the photo.'
        : (err?.message || 'Could not process the image.'));
      return;
    }
    try {
      await onConfirm?.(file);
      onOpenChange?.(false);
    } catch {
      // The caller owns its own error toast (upload failures).
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="photo-crop-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Crop to a square and rotate if needed. Manual photos replace the resume photo when both exist.
          </DialogDescription>
        </DialogHeader>

        <div
          ref={stageRef}
          className={`relative mx-auto h-64 w-64 overflow-hidden border bg-secondary ${cropMode ? 'rounded-md' : 'rounded-full'}`}
          data-testid="photo-crop-stage"
        >
          <div className="pointer-events-none absolute inset-0 grid place-items-center" style={appliedCropStyle}>
            {imageSrc ? (
              <img
                ref={imgRef}
                src={imageSrc}
                alt=""
                onLoad={onImgLoad}
                style={previewStyle}
                className="select-none"
                draggable={false}
                data-testid="photo-crop-preview"
              />
            ) : null}
          </div>

          {cropMode && cropRect ? (
            <div
              className="absolute cursor-move touch-none border-2 border-white"
              style={{
                left: cropRect.x,
                top: cropRect.y,
                width: cropRect.size,
                height: cropRect.size,
                boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.55)',
              }}
              role="group"
              tabIndex={0}
              aria-label="Crop area — drag to reposition, arrow keys to nudge"
              onKeyDown={onSelectionKeyDown}
              data-testid="photo-crop-selection"
              {...dragProps('move')}
            >
              {/* Hairlines either side of the white border keep the edge readable on
                  both bright and dark photos; thirds guides read as a crop region. */}
              <div className="pointer-events-none absolute -inset-[3px] border border-slate-900/60" aria-hidden="true" />
              <div className="pointer-events-none absolute inset-0 border border-slate-900/30" aria-hidden="true">
                <div className="absolute inset-y-0 left-1/3 w-px bg-white/40" />
                <div className="absolute inset-y-0 left-2/3 w-px bg-white/40" />
                <div className="absolute inset-x-0 top-1/3 h-px bg-white/40" />
                <div className="absolute inset-x-0 top-2/3 h-px bg-white/40" />
              </div>
              {HANDLES.map((handle) => (
                <div
                  key={handle.id}
                  className={`absolute h-3.5 w-3.5 touch-none rounded-sm border border-slate-500 bg-white shadow ${handle.className}`}
                  data-testid={`photo-crop-handle-${handle.id}`}
                  {...dragProps(handle.id)}
                />
              ))}
            </div>
          ) : null}
        </div>

        {cropMode ? (
          <p className="text-center text-xs text-muted-foreground">
            Drag the box to reposition, or drag a corner to resize.
          </p>
        ) : null}

        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button
            type="button"
            variant={cropMode ? 'default' : 'outline'}
            size="sm"
            aria-pressed={cropMode}
            onClick={() => setCropMode((v) => !v)}
            disabled={!natural.w}
            data-testid="photo-crop-toggle"
          >
            <Crop className="mr-1.5 h-4 w-4" /> Crop
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setRotation((r) => (r + 90) % 360);
              dropCrop();
            }}
            data-testid="photo-crop-rotate"
          >
            <RotateCw className="mr-1.5 h-4 w-4" /> Rotate
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => {
              setZoom((z) => Math.max(1, Math.round((z - 0.1) * 10) / 10));
              dropCrop();
            }}
            disabled={zoom <= 1}
            data-testid="photo-crop-zoom-out"
            aria-label="Zoom out"
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => {
              setZoom((z) => Math.min(3, Math.round((z + 0.1) * 10) / 10));
              dropCrop();
            }}
            disabled={zoom >= 3}
            data-testid="photo-crop-zoom-in"
            aria-label="Zoom in"
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          {cropMode ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setCropRect(imgBox ? defaultCrop(imgBox) : null)}
              disabled={!imgBox}
              data-testid="photo-crop-reset"
            >
              Reset
            </Button>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange?.(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={busy || !natural.w}
            data-testid="photo-crop-confirm"
          >
            {busy ? 'Saving…' : 'Use photo'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
